import { FocusEvent, forwardRef, useRef } from "react";

import { setForwardedRef } from "@libs/utils/utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import {
  DocumentEditor,
  DocumentEditorContainer,
  DocumentEditorContainerComponent,
  SpellChecker,
  TextExport,
  Toolbar,
  ToolbarItem,
  WordExport
} from "@syncfusion/ej2-react-documenteditor";
import "@ui-components/styles/syncfusion.css";

import { AutofillContext } from "./editor-autofills/editor-autofill.types.ts";
import { SyncfusionEditorAutofill } from "./editor-autofills/syncfusion-editor-autofill.ts";

const LANGUAGE_ID = 1033; // https://wiki.freepascal.org/Language_Codes
interface SyncFusionWordProcessorProps
  extends Partial<DocumentEditorContainer> {
  /** Returns the data as JSON string **/
  onContentChanged: (value: string) => void;
  onFocus?: (evt: FocusEvent) => void;
  onBlur?: (evt: FocusEvent) => void;
  withClinicalAutofill?: boolean;
  autofillContext: AutofillContext;
}

const defaultToolbarOptions: ToolbarItem[] = [
  "Undo",
  "Redo",
  "Separator",
  "Image",
  "Table",
  "Hyperlink",
  "Separator",
  "Header",
  "Footer",
  "PageSetup",
  "PageNumber",
  "Break",
  "Separator",
  "Find",
  "Separator",
  "Separator",
  "Separator"
];

DocumentEditorContainerComponent.Inject(
  Toolbar,
  WordExport,
  TextExport,
  SpellChecker
);

export const SyncFusionWordProcessor = forwardRef<
  DocumentEditorContainerComponent,
  SyncFusionWordProcessorProps
>(
  (
    {
      onContentChanged,
      onFocus,
      onBlur,
      withClinicalAutofill = true,
      autofillContext,
      ...rest
    },
    ref
  ) => {
    const isEditorInitialized = useRef(false);
    const isSpellcheckInitialized = useRef(false);
    const root = useStores();
    const autofill = new SyncfusionEditorAutofill(root, autofillContext);

    // Syncfusion Safety
    // - Only enable spellcheck if there is a service URL configured
    // - Don't allow use of the public service URL
    // To re-enable spellcheck, when we have a private Syncfusion service,
    // just add syncFusionDocumentEditorServiceUrl to the configuration.
    const serviceUrl =
      window.appConfig?.syncFusionDocumentEditorServiceUrl?.trim();

    const enableSpellCheck = Boolean(serviceUrl);
    if (
      serviceUrl &&
      new URL(serviceUrl).hostname === "ej2services.syncfusion.com"
    ) {
      throw new Error(
        "The public Syncfusion Document Editor service must not be used."
      );
    }

    const initSpellChecker = (documentEditor: DocumentEditor) => {
      if (!enableSpellCheck) {
        return;
      }

      const spellChecker = documentEditor.spellChecker;
      if (spellChecker.enableSpellCheck && !isSpellcheckInitialized.current) {
        // Set language id to map dictionary in server side
        spellChecker.languageID = LANGUAGE_ID;
        spellChecker.removeUnderline = false;
        // Allow suggestion for misspelled word
        spellChecker.allowSpellCheckAndSuggestion = true;
        isSpellcheckInitialized.current = true;
      }
    };
    return (
      <DocumentEditorContainerComponent
        ref={instance => {
          if (instance) {
            initSpellChecker(instance.documentEditor);
            if (withClinicalAutofill) {
              autofill
                .setDocumentEditor(instance.documentEditor)
                .jumpLinksHandler();

              autofill.replace();
            }

            setForwardedRef<DocumentEditorContainerComponent | null>(
              ref,
              instance
            );
          }
        }}
        contentChange={({ source }: { source: DocumentEditorContainer }) => {
          isEditorInitialized.current &&
            onContentChanged(source.documentEditor.serialize());
        }}
        created={() => {
          isEditorInitialized.current = true;
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        toolbarItems={defaultToolbarOptions}
        enableToolbar
        height="auto"
        {...rest}
        // Syncfusion Safety
        // - Don't allow caller to override serviceUrl or enableSpellCheck
        serviceUrl={serviceUrl || undefined}
        enableSpellCheck={enableSpellCheck}
      />
    );
  }
);
